import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { labAPI, auditLogAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const LabTaskHistory = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    loadTaskHistory();
  }, [id]);

  const loadTaskHistory = async () => {
    try {
      setLoading(true);
      const taskResponse = await labAPI.getOne(id);
      setTask(taskResponse.data.data);

      // Fetch audit logs for this task
      const logsResponse = await auditLogAPI.getAll({ 
        model: 'Lab',
        record_id: id,
        limit: 100
      });
      setAuditLogs(logsResponse.data.data);
    } catch (error) {
      showError(t('messages.error'));
      navigate('/lab');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'Create': '✨',
      'Update': '✏️',
      'Start Work': '▶️',
      'Complete': '✅',
      'Assign': '👤',
    };
    return icons[action] || '•';
  };

  const getActionColor = (action) => {
    const colors = {
      'Create': 'bg-blue-100 text-blue-800',
      'Update': 'bg-yellow-100 text-yellow-800',
      'Start Work': 'bg-orange-100 text-orange-800',
      'Complete': 'bg-green-100 text-green-800',
      'Assign': 'bg-purple-100 text-purple-800',
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('lab.taskHistoryTitle')}</h2>
          <p className="text-gray-600 mt-1">{t('lab.taskHistorySubtitle', { labNumber: task?.lab_number })}</p>
        </div>
        <button
          onClick={() => navigate(`/lab/${id}`)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('lab.backToTask')}
        </button>
      </div>

      {/* Task Summary Card */}
      {task && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">{t('lab.currentStatus')}</p>
              <p className="font-semibold text-gray-900 mt-1">{task.status}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('lab.createdLabel')}</p>
              <p className="font-semibold text-gray-900 mt-1">
                {new Date(task.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('lab.startedLabel')}</p>
              <p className="font-semibold text-gray-900 mt-1">
                {task.started_at ? new Date(task.started_at).toLocaleString() : t('common.notStarted')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('lab.completedLabel')}</p>
              <p className="font-semibold text-gray-900 mt-1">
                {task.completed_at ? new Date(task.completed_at).toLocaleString() : t('common.notCompleted')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow-lg p-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('lab.activityTimeline')}</h3>

        {auditLogs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('lab.noActivityRecords')}</p>
        ) : (
          <div className="space-y-6">
            {auditLogs.map((log, index) => {
              const isLast = index === auditLogs.length - 1;
              return (
                <div key={log.id} className="flex">
                  {/* Timeline line and dot */}
                  <div className="flex flex-col items-center mr-6">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-lg ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    {!isLast && (
                      <div className="w-0.5 h-16 bg-gray-200 my-2"></div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <div className="flex items-baseline gap-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="text-sm text-gray-600">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>

                    <p className="text-sm text-gray-900 mt-2 font-medium">
                      {t('common.by')}: {log.user?.full_name || t('common.system')}
                    </p>

                    {log.description && (
                      <p className="text-sm text-gray-600 mt-1">{log.description}</p>
                    )}

                    {/* Show changes if available */}
                    {log.old_values && log.new_values && (
                      <div className="mt-3 bg-gray-50 rounded p-3 text-sm">
                        <details>
                          <summary className="cursor-pointer font-medium text-gray-700">
                            {t('lab.viewChanges')}
                          </summary>
                          <div className="mt-2 space-y-1">
                            {Object.keys(log.new_values).map(key => (
                              log.old_values[key] !== log.new_values[key] && (
                                <div key={key} className="text-xs">
                                  <span className="font-medium">{key}:</span>
                                  <span className="text-red-600"> {log.old_values[key]}</span>
                                  <span className="text-gray-400"> → </span>
                                  <span className="text-green-600">{log.new_values[key]}</span>
                                </div>
                              )
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabTaskHistory;
